import React,{ useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createSortingService, SortState } from "../../Services/SortingServices";
import EditProfilePopup from "../../PopUp/DoctorProfilePopup/EditProfilePopup";
import OriginalProfile from "../../PopUp/DoctorProfilePopup/OriginalProfile";
import StatusBadge from "../../Common/StatusBadge";
import DateFilterPopup, { DateFilterValue } from "../../Services/DateFilterPopup/DateFilterPopup";
import Button from "../../Common/Button";
import Pagination from "../../Services/PaginationControl/Pagination";
import { getAllDoctorsAPI } from "../../Api/MarketingApi";
import dayjs from "dayjs"; // ✅ Added for date formatting
import DoctorTATRow from "../Audit/DoctorProfileAudit/DoctorProfileAudit";
import DoctorProfileTAT from "../Audit/DoctorProfileTAT/DoctorProfileTAT";
type StatusType =
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'pending'
  | 'draft'
  | 'sendback'
  | 'recheck';
interface Doctor {
  doctorId: number;
  namePrefixCode: string ;
  name: string;
  designation: string | null;
  experienceYears: number;
  locationName: string;
  specialization: string | null;
  doctorStatus?: string;
  createdOn: string;
  overdueDays: number;
}

interface DoctorsAPIResponse {
  isEdit: boolean;
  isView: boolean;
  isDelete: boolean;
  doctorlist: Doctor[];
}

type SortKey = keyof Pick<
  Doctor,
  "name" | "designation" | "experienceYears" | "locationName" | "specialization" | "overdueDays" | "doctorStatus" | "createdOn" // ✅ Added
>;

const sortingService = createSortingService<Doctor>();
const roleName = sessionStorage.getItem("roleName");

const DoctorProfiles = () => {
  const navigate = useNavigate();
  const [doctorsData, setDoctorsData] = useState<DoctorsAPIResponse>({
    isEdit: false,
    isView: false,
    isDelete: false,
    doctorlist: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string>("");
  const [sortState, setSortState] = useState<SortState<Doctor>>({
    sortKey: "name",
    sortOrder: "asc"
  });
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilterValue>({
    type: "",
    from: "",
    to: "",
  });
  const [filters, setFilters] = useState({
    name: "",
    designation: "",
    experience: "",
    locationName: "",
    specialization: "",
    overdueDays: "",
    doctorStatus: "",
    createdOn: "",
  });

  const [globalSearch, setGlobalSearch] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [expandedDoctorId, setExpandedDoctorId] = useState<number | null>(null);
  const [showTATModal, setShowTATModal] = useState(false);
  const [selectedDoctorForTAT, setSelectedDoctorForTAT] = useState<{id: number; name: string} | null>(null);
  const fetchDoctors = async () => {
    try {
      setIsLoading(true);
      setApiError("");
      const response = await getAllDoctorsAPI(roleName);
      
      
      if (response.data) {
        setDoctorsData({
          isEdit: response.data.isEdit || false,
          isView: response.data.isView || false,
          isDelete: response.data.isDelete || false,
          doctorlist: response.data.doctorlist || []
        });
      }
    } catch (error: any) {
      console.error("Error fetching doctors:", error);
      setApiError(
        error?.response?.data?.message || 
        error?.message || 
        "Failed to fetch doctors data"
      );
    } finally {
      setIsLoading(false);
    }
  };
const handleRowToggle = (doctorId: number) => {
  setExpandedDoctorId(prev =>
    prev === doctorId ? null : doctorId
  );
};
  const getSerialNumber = (index: number) => {
    return (currentPage - 1) * itemsPerPage + index + 1;
  };
  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleViewDoctor = (doctorId: number) => {
    setIsViewModalOpen(true);
    setSelectedDoctorId(doctorId);
  };

  const handleEditDoctor = (doctorId: number) => {
    setIsEditModalOpen(true);
    setSelectedDoctorId(doctorId);
  };

  const handleCloseModal = () => {
    fetchDoctors();
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedDoctorId(null);
  };

  const handleAuditDoctor = (id: number, name: string) => {
  setSelectedDoctorForTAT({ id, name });
  setShowTATModal(true);
};

  const handleDeleteDoctor = (doctorId: number) => {
  };

  const getFilteredDoctors = (): Doctor[] => {
    if (!doctorsData.doctorlist || !Array.isArray(doctorsData.doctorlist)) {
      return [];
    }
    
    let data = [...doctorsData.doctorlist];
    
    // Global search filter
    if (globalSearch.trim()) {
      const search = globalSearch.toLowerCase();
      data = data.filter(doc =>
        [doc.name, doc.designation || "", doc.locationName, doc.specialization || "", doc.doctorStatus || "", doc.createdOn || ""]
          .join(" ")
          .toLowerCase()
          .includes(search)
      );
    }

    // Date filter logic
    if (dateFilter.type) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      data = data.filter(doc => {
        if (!doc.createdOn || doc.createdOn.startsWith('0001-01-01')) {
          return false;
        }

        let docDate: Date;
        if (doc.createdOn.includes('T')) {
          docDate = new Date(doc.createdOn);
        } else {
          const [day, month, year] = doc.createdOn.split('/').map(Number);
          docDate = new Date(year, month - 1, day);
        }
        docDate.setHours(0, 0, 0, 0);

        switch (dateFilter.type) {
          case "today":
            return docDate.getTime() === today.getTime();
          case "yesterday": {
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            return docDate.getTime() === yesterday.getTime();
          }
          case "last7days": {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            return docDate >= sevenDaysAgo && docDate <= today;
          }
          case "last30days": {
            const thirtyDaysAgo = new Date(today);
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return docDate >= thirtyDaysAgo && docDate <= today;
          }
          case "thismonth": {
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return docDate >= firstDayOfMonth && docDate <= lastDayOfMonth;
          }
          case "lastmonth": {
            const firstDayOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            return docDate >= firstDayOfLastMonth && docDate <= lastDayOfLastMonth;
          }
          case "custom": {
            if (dateFilter.from && dateFilter.to) {
              const [fromDay, fromMonth, fromYear] = dateFilter.from.split('/').map(Number);
              const fromDate = new Date(fromYear, fromMonth - 1, fromDay);
              fromDate.setHours(0, 0, 0, 0);

              const [toDay, toMonth, toYear] = dateFilter.to.split('/').map(Number);
              const toDate = new Date(toYear, toMonth - 1, toDay);
              toDate.setHours(0, 0, 0, 0);

              return docDate >= fromDate && docDate <= toDate;
            }
            return true;
          }
          default:
            return true;
        }
      });
    }

    // Individual filters - ✅ Added createdOn filter
    return data.filter(doc => {
      const experienceFilter = filters.experience.trim();
      const overdueFilter = filters.overdueDays.trim();
      const createdOnFilter = filters.createdOn.trim();
      
      const matchesExperience =
        experienceFilter === "" ||
        doc.experienceYears.toString().includes(experienceFilter) ||
        (!isNaN(parseInt(experienceFilter))
          ? doc.experienceYears >= parseInt(experienceFilter)
          : true);
      
      const matchesOverdue =
        overdueFilter === "" ||
        doc.overdueDays.toString().includes(overdueFilter) ||
        (!isNaN(parseInt(overdueFilter))
          ? doc.overdueDays >= parseInt(overdueFilter)
          : true);

      const matchesCreatedOn =
        createdOnFilter === "" ||
        doc.createdOn.includes(createdOnFilter);

      return (
        doc.name.toLowerCase().includes(filters.name.toLowerCase()) &&
        (doc.designation || "").toLowerCase().includes(filters.designation.toLowerCase()) &&
        (doc.specialization || "").toLowerCase().includes(filters.specialization.toLowerCase()) &&
        matchesExperience &&
        matchesOverdue &&
        matchesCreatedOn && // ✅ Added
        doc.locationName.toLowerCase().includes(filters.locationName.toLowerCase()) &&
        (doc.doctorStatus || "").toLowerCase().includes(filters.doctorStatus.toLowerCase())
      );
    });
  };

  const filteredDoctors = getFilteredDoctors();
  const filteredAndSortedDoctors = sortingService.sortData(filteredDoctors, sortState);
  const totalPages = Math.ceil(filteredAndSortedDoctors.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDoctors = filteredAndSortedDoctors.slice(startIndex, endIndex);
  const handleSort = sortingService.createSortHandler(sortState, setSortState);
  const sortIcon = (key: SortKey) => sortingService.getSortIcon(sortState, key);

  const handleClearFilters = () => {
    setFilters({
      name: "",
      designation: "",
      experience: "",
      locationName: "",
      specialization: "",
      overdueDays: "",
      doctorStatus: "",
      createdOn: "", // ✅ Added
    });
    setGlobalSearch("");
    setDateFilter({
      type: "",
      from: "",
      to: "",
    });
  };

  if (apiError) {
    return (
      <div className="bg-gray-50 p-8">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="text-red-600 mb-4">⚠️ Error</div>
          <p className="text-red-800">{apiError}</p>
          
          <button
            onClick={fetchDoctors}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

const mapDoctorStatus = (status: string): StatusType => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'pending';
    case 'save draft':
      return 'draft';
    case 'send back':
      return 'sendback';
    case 'submitted':
      return 'submitted';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'rejected';
      case 'recheck':
      return 'recheck';
    default:
      return 'pending';
  }
};
  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-3">
      {isEditModalOpen && selectedDoctorId !== null && (
        <EditProfilePopup
          selectedDoctorId={selectedDoctorId}
          onClose={handleCloseModal}
        />
      )}
      {isViewModalOpen && selectedDoctorId !== null && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm p-4"
          onClick={() => setIsViewModalOpen(false)}
        >
          <div
            className="relative bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <OriginalProfile doctorId={selectedDoctorId} />
          </div>
        </div>
      )}
      {showTATModal && selectedDoctorForTAT && (
<DoctorProfileTAT
  doctorId={selectedDoctorForTAT.id}
    doctorName={selectedDoctorForTAT.name}
    isOpen={showTATModal}
    onClose={() => {
      setShowTATModal(false);
      setSelectedDoctorForTAT(null);
    }}
  />
)}

      {/* Main Container */}
      <div className="">
        {/* Header Section */}
        <div className="mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Doctor Profiles</h1>
              <p className="text-gray-600 text-sm">Manage and view doctor information</p>
            </div>
            <div className="flex flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto sm:min-w-[250px] md:min-w-[300px] lg:flex-1 lg:max-w-md">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search doctors..."
                  value={globalSearch}
                  onChange={e => setGlobalSearch(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-1.5 border border-gray-300 rounded-lg text-sm bg-white placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#184A84] focus:border-[#184A84] transition-all duration-200"
                />
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`px-4 py-1.5 flex items-center gap-2 border rounded-lg text-sm font-medium transition-all ${
                    dateFilter.type 
                      ? 'bg-blue-50 border-blue-300 text-blue-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {dateFilter.type && (
                    <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                      Active
                    </span>
                  )}
                </button>

                {showDateFilter && (
                  <DateFilterPopup
                    isOpen={showDateFilter}
                    onClose={() => setShowDateFilter(false)}
                    initialValue={dateFilter}
                    onApply={(value) => {
                      setDateFilter(value);
                    }}
                  />
                )}
              </div>
              <Button 
                variant="secondary"
                size="sm"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto custom-scrollbar">
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto custom-scrollbar">
              <table className="w-full text-sm">
                {/* Header */}
               <thead className="bg-[#ecf0f1] sticky top-0 z-20 font-normal">
                <tr className=" ">
                  <th className="px-3 py-3 text-left w-16">S.No</th>
                  <th className="px-3 py-3 text-left  cursor-pointer  group" onClick={() => handleSort("name")}>
                    <div className="flex items-center gap-2  ">
                      <span className="">Doctor Name</span>
                      <span className="text-blue-500 group-hover:text-white-100">{sortIcon("name")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left   cursor-pointer    " onClick={() => handleSort("designation")}>
                    <div className="flex items-center gap-2">
                      <span>Designation</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("designation")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left  cursor-pointer   " onClick={() => handleSort("specialization")}>
                    <div className="flex items-center gap-2">
                      <span>Specialization</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("specialization")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left max-w-[100px]  cursor-pointer   " onClick={() => handleSort("experienceYears")}>
                    <div className="flex items-center gap-2">
                      <span>Experience</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("experienceYears")}</span>
                    </div>
                  </th>
                  {/* ✅ NEW: Created On Column */}
                  <th className="px-3 py-3 text-left  cursor-pointer   transition-colors group whitespace-nowrap" onClick={() => handleSort("createdOn")}>
                    <div className="flex items-center gap-2">
                      <span>Created On</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("createdOn")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left  cursor-pointer   transition-colors group" onClick={() => handleSort("locationName")}>
                    <div className="flex items-center gap-2">
                      <span>Location</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("locationName")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left  cursor-pointer   transition-colors group max-w-[100px]" onClick={() => handleSort("overdueDays")}>
                    <div className="flex items-center gap-2">
                      <span>Overdue</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("overdueDays")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left  cursor-pointer   transition-colors group max-w-[100px]" onClick={() => handleSort("doctorStatus")}>
                    <div className="flex items-center gap-2">
                      <span>Status</span>
                      <span className="text-blue-500 group-hover:text-blue-100">{sortIcon("doctorStatus")}</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center font-semibold">Actions</th>
                </tr>
                <tr className="bg-gray-50 border-t border-gray-200">
                  <th className="px-1 py-1 w-16"></th>
                  <th className="px-1 py-1">
                    <input 
                      type="text" 
                      placeholder="Filter by name..." 
                      value={filters.name} 
                      onChange={e => setFilters(prev => ({ ...prev, name: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1">
                    <input 
                      type="text" 
                      placeholder="Filter by designation..." 
                      value={filters.designation} 
                      onChange={e => setFilters(prev => ({ ...prev, designation: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1">
                    <input 
                      type="text" 
                      placeholder="Filter by specialization..." 
                      value={filters.specialization} 
                      onChange={e => setFilters(prev => ({ ...prev, specialization: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1 max-w-[100px]">
                    <input 
                      type="text" 
                      placeholder="Min years..." 
                      value={filters.experience} 
                      onChange={e => setFilters(prev => ({ ...prev, experience: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  {/* ✅ NEW: Created On Filter */}
                  <th className="px-1 py-1 whitespace-nowrap">
                    <input 
                      type="date" 
                      value={filters.createdOn} 
                      onChange={e => setFilters(prev => ({ ...prev, createdOn: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1">
                    <input 
                      type="text" 
                      placeholder="Filter by location..." 
                      value={filters.locationName} 
                      onChange={e => setFilters(prev => ({ ...prev, locationName: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1 max-w-[100px]">
                    <input 
                      type="text" 
                      placeholder="Min days..." 
                      value={filters.overdueDays} 
                      onChange={e => setFilters(prev => ({ ...prev, overdueDays: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th className="px-1 py-1 max-w-[100px]">
                    <input 
                      type="text" 
                      placeholder="Filter by status..." 
                      value={filters.doctorStatus} 
                      onChange={e => setFilters(prev => ({ ...prev, doctorStatus: e.target.value }))} 
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" 
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>

                {/* Body */}
                <tbody className="divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-16 text-center"> {/* ✅ Updated: 9 → 10 */}
                        <div className="flex flex-col items-center gap-3">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#184A84]"></div>
                          <p className="text-gray-500">Loading doctors...</p>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedDoctors.length > 0 ?
                     (
    paginatedDoctors.map((doc, index) => (
      <React.Fragment key={doc.doctorId}>
        <tr
          onClick={() => handleRowToggle(doc.doctorId)}
          className={`cursor-pointer transition-all duration-300 min-w-[1100px]
            hover:bg-blue-50
            ${expandedDoctorId === doc.doctorId ? "bg-blue-50" : ""}
          `}
        >
          <td className="px-3 py-3 text-center">
            {getSerialNumber(index)}
          </td>

          <td className="px-2 py-3 font-semibold text-gray-900">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">
                {doc.name.charAt(0)}
              </div>
              <span className="text-xs">{doc.namePrefixCode && `${doc.namePrefixCode}\u00A0`}{doc.name}</span>
            </div>
          </td>

          <td className="px-3 py-3 text-xs">{doc.designation || "-"}</td>

          <td className="px-3 py-3">
            <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800">
              {doc.specialization || "-"}
            </span>
          </td>

          <td className="px-3 py-3">
            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
              {doc.experienceYears} yrs
            </span>
          </td>

          <td className="px-3 py-3 text-xs whitespace-nowrap">
            {dayjs(doc.createdOn).format("DD MMM YY")}
          </td>

          <td className="px-3 py-3 text-xs">{doc.locationName}</td>

          <td className="px-3 py-3">
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                doc.overdueDays > 7
                  ? "bg-red-100 text-red-800"
                  : doc.overdueDays > 3
                  ? "bg-orange-100 text-orange-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {doc.overdueDays} days
            </span>
          </td>

          <td className="px-3 py-3">
           {doc.doctorStatus ? (
<StatusBadge status={mapDoctorStatus(doc.doctorStatus)} />  ) : (
    <span className="text-gray-400 text-xs">-</span>
  )}
          </td>

             <td className="px-3 py-3">
                            <div className="flex items-center justify-center gap-1">
                              {doctorsData.isView && (
                                <button
                                  onClick={() => handleViewDoctor(doc.doctorId)}
                                  title={`View ${doc.name}`}
                                  className="p-1 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 hover:text-blue-900 transition-all hover:scale-110"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </button>
                              )}
                             {doctorsData.isEdit && doc.doctorStatus?.toLowerCase() !== 'approved' && (
  <button
    onClick={() => handleEditDoctor(doc.doctorId)}
    title={`Edit ${doc.name}`}
    className="p-1 rounded-lg bg-green-100 hover:bg-green-200 text-green-600 hover:text-green-900 transition-all hover:scale-110"
  >
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  </button>
)}

                              {doctorsData.isDelete && (
                                <button
                                  onClick={() => handleDeleteDoctor(doc.doctorId)}
                                  title={`Delete ${doc.name}`}
                                  className="p-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-900 transition-all hover:scale-110"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              )}
                              <button 
                                onClick={() => handleAuditDoctor(doc.doctorId,doc.name)} 
                                title={`View Audit Trail for ${doc.name}`}
                                className="p-1 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-600 hover:text-purple-900 transition-all hover:scale-110"
                                aria-label="View audit trail"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </button>
                            </div>
                          </td>
        </tr>

        {/* ================= EXPANDED ROW ================= */}
        {expandedDoctorId === doc.doctorId && (
  <tr className="bg-gray-50">
    <td colSpan={10} className="p-0">
      <div className="overflow-x-auto w-full custom-scrollbar">
        <div className="max-w-[700px]">
          <DoctorTATRow doctorId={doc.doctorId} />
        </div>
      </div>
    </td>
  </tr>
)}
      </React.Fragment>
    ))
  ): (
                    <tr>
                      <td colSpan={10} className="px-6 py-16 text-center"> 
                        <div className="flex flex-col items-center gap-3">
                          <svg
                            className="w-12 h-12 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                          </svg>
                          <p className="text-gray-500 font-medium">No doctors found</p>
                          <p className="text-gray-400 text-sm">Try adjusting your search or filter criteria</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredAndSortedDoctors.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
            showItemsPerPage={true}
            itemsPerPageOptions={[30, 50, 100, 150]}
          />
        </div>
      </div>
    </div>
  );
};

export default DoctorProfiles;
